import React, { useState } from "react";
import { Popover, Box } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit2,
  Pause,
  Copy,
  Ban,
  Trash2,
  Medal,
  Users,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { FiMoreVertical, FiChevronDown } from "react-icons/fi";
import AhmedAvatar from "../../../assets/images/alert2 1 (1).png";
import TierDetailsModal from "./TierDetailsModal";
import "./LoyaltyTiers.css";

export default function LoyaltyTiers() {

  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(dayjs("2025-05-01"));
  const [endDate, setEndDate] = useState(dayjs("2025-05-31"));
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [tierPage, setTierPage] = useState(1);
  const [perPage, setPerPage] = useState(5);

  const isDatePickerOpen = Boolean(dateAnchorEl);
  const handleDateClick = (e) => setDateAnchorEl(e.currentTarget);
  const handleDateClose = () => setDateAnchorEl(null);
  const dateRangeLabel = `${startDate.format("MMM DD, YYYY")} - ${endDate.format(
    "MMM DD, YYYY",
  )}`;

  const kpiStats = [
    {
      title: "Total Members",
      value: "24,568",
      icon: Users,
      bg: "#F3E8FF",
      iconColor: "#A855F7",
      subtitle: "All tiers",
    },
    {
      title: "Active Tiers",
      value: "4",
      icon: CheckCircle2,
      bg: "#E6F7F4",
      iconColor: "#14B8A6",
      subtitle: "of 6 total",
    },
    {
      title: "Tier Upgrades",
      value: "1,248",
      icon: TrendingUp,
      bg: "#E6F7F4",
      iconColor: "#14B8A6",
      trend: "+14%",
      trendType: "up",
      subtitle: "vs last 30 days",
    },
    {
      title: "Tier Downgrades",
      value: "342",
      icon: TrendingDown,
      bg: "#FDEBEC",
      iconColor: "#DC2626",
      trend: "-14%",
      trendType: "down",
      subtitle: "vs last 30 days",
    },
  ];

  const tiersSummary = [
    {
      name: "Bronze",
      medalColor: "#CD7F32",
      members: 11250,
      pct: 47.6,
      benefits: 3,
      retention: 82,
      barColor: "#D97B3F",
    },
    {
      name: "Silver",
      medalColor: "#A7A9AC",
      members: 7120,
      pct: 30.1,
      benefits: 5,
      retention: 78,
      barColor: "#A7B08C",
    },
    {
      name: "Gold",
      medalColor: "#E8B923",
      members: 4200,
      pct: 17.8,
      benefits: 8,
      retention: 76,
      barColor: "#F0B90B",
    },
    {
      name: "Platinum",
      medalColor: "#4A5A78",
      members: 1050,
      pct: 4.4,
      benefits: 12,
      retention: 81,
      barColor: "#38507A",
    },
  ];

  const tierDistributionData = [
    { label: "Bronze", count: 11250, pct: 47.6, color: "#D97B3F" },
    { label: "Silver", count: 7120, pct: 30.1, color: "#A7A9AC" },
    { label: "Gold", count: 4200, pct: 17.8, color: "#F0B90B" },
    { label: "Platinum", count: 1050, pct: 4.4, color: "#38507A" },
  ];
  const totalMembers = 24568;

  const buildConicGradient = (segments) => {
    let acc = 0;
    const stops = segments.map((s) => {
      const start = acc;
      acc += s.pct;
      return `${s.color} ${start}% ${acc}%`;
    });
    return `conic-gradient(${stops.join(", ")})`;
  };

  const topBenefitsUsed = [
    { num: "1", label: "Free Car Wash", value: 8420, pct: 71 },
    { num: "2", label: "Double Points", value: 7210, pct: 61 },
    { num: "3", label: "10% Discount", value: 5830, pct: 49 },
    { num: "4", label: "Free Delivery", value: 2950, pct: 25 },
  ];

  const recentTierActivity = [
    {
      name: "Ahmed Mohamed",
      avatar: AhmedAvatar,
      from: "Silver",
      to: "Gold",
      type: "Upgrades",
      time: "20m ago",
    },
    {
      name: "Ahmed Mohamed",
      avatar: AhmedAvatar,
      from: "Gold",
      to: "Platinum",
      type: "Upgrades",
      time: "20m ago",
    },
    {
      name: "Ahmed Mohamed",
      avatar: AhmedAvatar,
      from: "Gold",
      to: "Silver",
      type: "Downgrades",
      time: "20m ago",
    },
  ];

  const upgradesDowngradesData = [
    { name: "Apr 28", upgrades: 95000, downgrades: 65000 },
    { name: "May 05", upgrades: 130000, downgrades: 60000 },
    { name: "May 12", upgrades: 125000, downgrades: 105000 },
    { name: "May 19", upgrades: 160000, downgrades: 75000 },
    { name: "May 26", upgrades: 140000, downgrades: 95000 },
    { name: "May 31", upgrades: 195000, downgrades: 100000 },
  ];

  const tiersData = [
    {
      id: "TR-001",
      name: "Bronze",
      medalColor: "#CD7F32",
      members: 11250,
      pctOfTotal: 47.6,
      entryRule: "Default",
      entryRuleType: "default",
      upgradeRule: "Spend 2,000 EGP or 2,000 pts",
      benefits: 3,
      avgStay: "69 Days",
      retention: 82,
      status: "Active",
      lastUpdate: "2h ago",
      updatedBy: "Mona Ali",
    },
    {
      id: "TR-002",
      name: "Silver",
      medalColor: "#A7A9AC",
      members: 7120,
      pctOfTotal: 30.1,
      entryRule: "Default",
      entryRuleType: "default",
      upgradeRule: "Spend 5,000 EGP or 5,000 pts",
      benefits: 5,
      avgStay: "91 Days",
      retention: 78,
      status: "Active",
      lastUpdate: "2h ago",
      updatedBy: "Mona Ali",
    },
    {
      id: "TR-003",
      name: "Gold",
      medalColor: "#E8B923",
      members: 4200,
      pctOfTotal: 17.8,
      entryRule: "By Rule",
      entryRuleType: "byrule",
      upgradeRule: "Spend 10,000 EGP or 10,000 pts",
      benefits: 8,
      avgStay: "128 Days",
      retention: 76,
      status: "Active",
      lastUpdate: "2h ago",
      updatedBy: "Mona Ali",
    },
    {
      id: "TR-004",
      name: "Platinum",
      medalColor: "#4A5A78",
      members: 1050,
      pctOfTotal: 4.4,
      entryRule: "By Rule",
      entryRuleType: "byrule",
      upgradeRule: "Spend 20,000 EGP or 20,000 pts",
      benefits: 12,
      avgStay: "215 Days",
      retention: 81,
      status: "Active",
      lastUpdate: "2h ago",
      updatedBy: "Mona Ali",
    },
  ];

  const totalTiersCount = tiersData.length;
  const totalPages = Math.max(1, Math.ceil(totalTiersCount / perPage));
  const startIndex = (tierPage - 1) * perPage;
  const currentPageData = tiersData.slice(startIndex, startIndex + perPage);

  const statusStyles = {
    Active: { bg: "#E6F7F4", color: "#14B8A6" },
    Inactive: { bg: "#F3F4F6", color: "#6B7280" },
  };

  const getPageNumbers = (current, total) => {
    const pages = [];
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  };
  const pageNumbers = getPageNumbers(tierPage, totalPages);

  return (
    <div className="voucher-page-wrapper">
      <div className="voucher-overview">
        {/* KPI CARDS */}
        <div className="lt-kpi-grid">
          {kpiStats.map((item, i) => {
            const Icon = item.icon;
            return (
              <div className="lt-kpi-card" key={i}>
                <div className="lt-kpi-top">
                  <div
                    className="lt-kpi-icon"
                    style={{ backgroundColor: item.bg }}
                  >
                    <Icon size={18} color={item.iconColor} />
                  </div>
                  <span className="lt-kpi-title">{item.title}</span>
                </div>
                <div className="lt-kpi-value">{item.value}</div>
                <div className="lt-kpi-bottom">
                  {item.trend ? (
                    <>
                      <span className={`lt-trend lt-trend-${item.trendType}`}>
                        {item.trend}
                      </span>
                      <span className="lt-trend-text">{item.subtitle}</span>
                    </>
                  ) : (
                    <span className="lt-trend-text">{item.subtitle}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {/* TIER SUMMARY CARDS */}
        <div className="lt-summary-grid my-3">
          {tiersSummary.map((t, i) => (
            <div className="lt-summary-card" key={i}>
              <div className="lt-summary-top">
                <Medal size={18} color={t.medalColor} />
                <span className="lt-summary-name">{t.name}</span>
              </div>
              <span className="lt-summary-label">Members</span>
              <div className="lt-summary-members-row">
                <span className="lt-summary-members">
                  {t.members.toLocaleString()}
                </span>
                <span className="lt-summary-pct">{t.pct}% of total</span>
              </div>
              <div className="lt-summary-stats-row">
                <div>
                  <span className="lt-summary-label">Benefits</span>
                  <div className="lt-summary-stat-value">{t.benefits}</div>
                </div>
                <div>
                  <span className="lt-summary-label">Retention</span>
                  <div className="lt-summary-stat-value">{t.retention}%</div>
                </div>
              </div>
              <div className="lt-summary-bar-bg">
                <div
                  className="lt-summary-bar-fill"
                  style={{
                    width: `${t.retention}%`,
                    backgroundColor: t.barColor,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        {/* THREE COLUMN INSIGHTS ROW */}
        <div className="lt-three-col-row">
          {/* Tier Distribution */}
          <div className="lt-card">
            <div className="lt-card-header">
              <h4 className="lt-card-title">Tier Distribution</h4>
            </div>
            <div className="lt-donut-body mt-1">
              <div className="lt-donut-wrapper">
                <div
                  className="lt-donut-ring"
                  style={{
                    background: buildConicGradient(tierDistributionData),
                  }}
                >
                  <div className="lt-donut-hole">
                    <span className="lt-donut-total">
                      {totalMembers.toLocaleString()}
                    </span>
                    <span className="lt-donut-total-label">Total Members</span>
                  </div>
                </div>
              </div>
              <div className="lt-donut-legend">
                {tierDistributionData.map((s, i) => (
                  <div className="lt-donut-legend-row" key={i}>
                    <span
                      className="lt-donut-legend-dot"
                      style={{ backgroundColor: s.color }}
                    />
                    <span className="lt-donut-legend-label">
                      {s.label} ({s.count.toLocaleString()})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Top Benefits Used */}
          <div className="lt-card">
            <div className="lt-card-header">
              <h4 className="lt-card-title">Top Benefits Used</h4>
              <span className="lt-view-all">
                View all <ArrowRight size={14} />
              </span>
            </div>
            <div className="lt-dist-list">
              {topBenefitsUsed.map((b, i) => (
                <div className="lt-dist-row" key={i}>
                  <div className="lt-dist-left">
                    <span className="lt-dist-num-circle">{b.num}</span>
                    <span className="lt-dist-label">{b.label}</span>
                  </div>
                  <div className="lt-dist-bar-bg">
                    <div
                      className="lt-dist-bar-fill"
                      style={{ width: `${b.pct}%` }}
                    />
                  </div>
                  <span className="lt-dist-pct">
                    {b.value.toLocaleString()} ({b.pct}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
          {/* Recent Tier Activity */}
          <div className="lt-card">
            <div className="lt-card-header">
              <h4 className="lt-card-title">Recent Tier Activity</h4>
              <span className="lt-view-all">
                View all <ArrowRight size={14} />
              </span>
            </div>
            <div className="lt-activity-list">
              {recentTierActivity.map((a, i) => (
                <div className="lt-activity-row" key={i}>
                  <div className="lt-activity-left">
                    <img
                      src={a.avatar}
                      alt={a.name}
                      className="lt-activity-avatar"
                    />
                    <span className="lt-activity-text">
                      <b>{a.name}</b>
                      <br />
                      {a.from} → {a.to}
                    </span>
                  </div>
                  <span
                    className={`lt-activity-badge ${
                      a.type === "Upgrades" ? "up" : "down"
                    }`}
                  >
                    {a.type === "Upgrades" ? (
                      <ArrowUpRight size={12} />
                    ) : (
                      <ArrowDownRight size={12} />
                    )}
                    {a.type}
                  </span>
                  <div className="lt-activity-right">
                    <span className="lt-activity-time">{a.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Upgrades vs Downgrades chart */}
        <div className="lt-card my-3">
          <div className="lt-card-header">
            <h4 className="lt-card-title">Upgrades vs Downgrades</h4>
            <button className="lt-period-select">
              Last 30 Days <FiChevronDown size={14} />
            </button>
          </div>

          <div className="lt-chart-legend">
            <span className="lt-legend-item">
              <span
                className="lt-legend-dot"
                style={{ backgroundColor: "#22C55E" }}
              />
              Upgrades
            </span>
            <span className="lt-legend-item">
              <span
                className="lt-legend-dot"
                style={{ backgroundColor: "#DC2626" }}
              />
              Downgrades
            </span>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart
              data={upgradesDowngradesData}
              margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
            >
              <defs>
                <linearGradient
                  id="ltUpgradesGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#3FBEAC" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#3FBEAC" stopOpacity={0} />
                </linearGradient>
                <linearGradient
                  id="ltDowngradesGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#3FBEAC" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="#3FBEAC" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid
                horizontal={true}
                vertical={true}
                strokeDasharray="4 4"
                stroke="#E5E7EB"
              />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#9CA3AF" }}
                tickMargin={8}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#9CA3AF" }}
                tickFormatter={(v) => (v === 0 ? "0" : `${v / 1000}K`)}
                domain={[0, 250000]}
                ticks={[0, 50000, 100000, 150000, 200000, 250000]}
                tickMargin={8}
              />
              <Tooltip />

              <Area
                type="monotone"
                dataKey="upgrades"
                stroke="none"
                fill="url(#ltUpgradesGradient)"
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="downgrades"
                stroke="none"
                fill="url(#ltDowngradesGradient)"
                isAnimationActive={false}
              />

              <Line
                type="monotone"
                dataKey="upgrades"
                name="Upgrades"
                stroke="#22C55E"
                strokeWidth={2}
                dot={{ r: 5, fill: "#fff", stroke: "#22C55E", strokeWidth: 2 }}
                activeDot={{
                  r: 6,
                  fill: "#fff",
                  stroke: "#22C55E",
                  strokeWidth: 2,
                }}
              />
              <Line
                type="monotone"
                dataKey="downgrades"
                name="Downgrades"
                stroke="#DC2626"
                strokeWidth={2}
                dot={{ r: 5, fill: "#fff", stroke: "#DC2626", strokeWidth: 2 }}
                activeDot={{
                  r: 6,
                  fill: "#fff",
                  stroke: "#DC2626",
                  strokeWidth: 2,
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {/* Filters */}
        <div className="lt-filters-bar mb-1">
          <div className="lt-filter-group">
            <label className="lt-table-header">All Tiers</label>

            <div className="lt-search-box">
              <Search size={16} color="#9CA3AF" />
              <input
                type="text"
                className="lt-search-input"
                placeholder="Search tier.."
              />
            </div>
          </div>

          <div className="lt-filters-right">
            <div className="lt-filter-group">
              <label className="lt-filter-label">Status</label>
              <select className="lt-filter-select" defaultValue="all">
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="lt-filter-group">
              <label className="lt-filter-label">Benefits</label>
              <select className="lt-filter-select" defaultValue="all">
                <option value="all">All</option>
              </select>
            </div>

            <div className="lt-filter-group">
              <label className="lt-filter-label">Members</label>
              <select className="lt-filter-select" defaultValue="all">
                <option value="all">All</option>
              </select>
            </div>

            <div className="lt-filter-group">
              <label className="lt-filter-label">Date Range</label>
              <button
                type="button"
                className="lt-date-range-btn"
                onClick={handleDateClick}
              >
                <span>{dateRangeLabel}</span>
                <Calendar size={16} color="#6B7280" />
              </button>

              <Popover
                open={isDatePickerOpen}
                anchorEl={dateAnchorEl}
                onClose={handleDateClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ p: 2, display: "flex", gap: 2 }}>
                    <DatePicker
                      label="From"
                      value={startDate}
                      onChange={(newValue) =>
                        newValue && setStartDate(newValue)
                      }
                      maxDate={endDate}
                      slotProps={{ textField: { size: "small" } }}
                    />
                    <DatePicker
                      label="To"
                      value={endDate}
                      onChange={(newValue) => newValue && setEndDate(newValue)}
                      minDate={startDate}
                      slotProps={{ textField: { size: "small" } }}
                    />
                  </Box>
                </LocalizationProvider>
              </Popover>
            </div>
          </div>
        </div>
        {/* Table */}
        <div className="lt-table-section">
          <div className="lt-table-card">
            <div className="lt-table-wrapper">
              <table className="lt-data-table">
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Members</th>
                    <th>Entry Rule</th>
                    <th>Upgrade Rule</th>
                    <th>Benefits</th>
                    <th>Avg. Stay</th>
                    <th>Retention</th>
                    <th>status</th>
                    <th>Last Updated</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.map((t) => {
                    const style = statusStyles[t.status] || {
                      bg: "#F3F4F6",
                      color: "#6B7280",
                    };
                    return (
                      <tr key={t.id}>
                        <td className="lt-name-cell">
                          <Medal size={16} color={t.medalColor} />
                          <span>{t.name}</span>
                        </td>
                        <td>
                          <div className="lt-members-cell">
                            <span>{t.members.toLocaleString()}</span>
                            <span className="lt-members-pct">
                              {t.pctOfTotal}%
                            </span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`lt-entry-badge lt-entry-${t.entryRuleType}`}
                          >
                            {t.entryRule}
                          </span>
                        </td>
                        <td>{t.upgradeRule}</td>
                        <td>{t.benefits}</td>
                        <td>{t.avgStay}</td>
                        <td>{t.retention}%</td>
                        <td>
                          <span
                            className="lt-status-badge"
                            style={{
                              backgroundColor: style.bg,
                              color: style.color,
                            }}
                          >
                            <span
                              className="lt-status-dot"
                              style={{ backgroundColor: style.color }}
                            />
                            {t.status}
                          </span>
                        </td>
                        <td className="lt-lastupdate-cell">
                          <div>{t.lastUpdate}</div>
                          <div className="lt-lastupdate-user">
                            {t.updatedBy}
                          </div>
                        </td>
                        <td className="lt-action-cell">
                          <FiMoreVertical
                            onClick={() =>
                              setOpenMenuId(openMenuId === t.id ? null : t.id)
                            }
                            size={16}
                            style={{ cursor: "pointer" }}
                          />
                          {openMenuId === t.id && (
                            <div className="lt-action-menu">
                          <button
                            className="lt-action-item"
                            onClick={() => {
                              setOpenMenuId(null);
                              setSelectedTier(t);
                              setIsDetailsOpen(true);
                            }}
                          >
                            <Eye size={14} /> View Details
                          </button>
                              <button
                                className="lt-action-item"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <Edit2 size={14} /> Edit
                              </button>
                              <button
                                className="lt-action-item lt-action-warning"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <Pause size={14} /> Pause
                              </button>
                              <button
                                className="lt-action-item"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <Copy size={14} /> Duplicate
                              </button>
                              <button
                                className="lt-action-item lt-action-danger"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <Ban size={14} /> Disable
                              </button>
                              <button
                                className="lt-action-item lt-action-danger"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <Trash2 size={14} /> Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="lt-table-footer">
              <span className="lt-showing-text">
                Showing {currentPageData.length} of {totalTiersCount} of{" "}
                {totalTiersCount} tiers
              </span>
              <div className="lt-footer-right">
                <select
                  className="lt-perpage-select"
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setTierPage(1);
                  }}
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                </select>
                <div className="lt-pagination">
                  <button
                    className="lt-page-arrow"
                    disabled={tierPage === 1}
                    onClick={() => setTierPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {pageNumbers.map((n, i) => (
                    <button
                      key={i}
                      className={`lt-page-btn ${tierPage === n ? "active" : ""}`}
                      onClick={() => setTierPage(n)}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    className="lt-page-arrow"
                    disabled={tierPage === totalPages}
                    onClick={() =>
                      setTierPage((p) => Math.min(totalPages, p + 1))
                    }
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TierDetailsModal
  open={isDetailsOpen}
  tier={selectedTier}
  onClose={() => setIsDetailsOpen(false)}
  onAction={(action, tier) => {
    console.log(action, tier);
  }}
/>
    </div>
  );
}
